import { TradeCard as ApiTradeCard, SetDetail, MatchIndexPackage } from 'store/services/trade/tradeApi';
import { TradeCard as ModalTradeCard } from '../components/InitiateTradeModal';

// One physical send routed to a specific want.
export interface AutoMatchAssignment {
  wantListId: number;
  haveRecordId: string;
  compositeId: string;
  cardId: number;
  foil: boolean;
  condition: string;
}

// Runtime-augmented card with auto-match metadata
export interface AutoMatchedCard extends ApiTradeCard {
  _autoMatchHaveCardId?: string;      // sender's printing MTGCard.id
  _autoMatchCompositeId?: string;     // representative compositeId (first assignment in group)
  _autoMatchAssignments?: AutoMatchAssignment[];  // one per sendable copy; drives trade-initiation routing
}

// ── 1. Expansion Logic — collapsed per (printing × foil), greedy-routed ────
//
// Input is the backend's compositeId list for this package (each "want-have"
// pair the backend says is compatible), matchDetails (cardId/foil/condition per
// composite), and haveQtyByCardId (sender's owned copies per printing, keyed by
// MTGCard.id). The backend over-emits composites: a single physical printing
// matching N wants appears N times, and it emits ONE composite per printing
// regardless of how many copies the sender owns. We:
//   1. Run greedy bipartite matching. Each printing (have-record) may route up
//      to its owned-copy count (haveQtyByCardId); each routed copy consumes one
//      unit of a want that still has remaining quantity. So a printing yields
//      min(owned copies, remaining want capacity) assignments. Unroutable copies
//      are dropped.
//   2. Group routed assignments by (cardId, foil).
//   3. Emit one AutoMatchedCard per group, quantity = group size = min(available,
//      wanted), carrying the list of assignments so trade-initiation can emit one
//      API row per copy.
export function expandAutoMatchCards(
  valuedCards: ApiTradeCard[],
  compositeIds: string[],
  matchDetails: Record<string, { condition: string; foil: boolean; cardId: number }> | undefined,
  haveQtyByCardId: Map<number, number>,
): AutoMatchedCard[] {
  if (compositeIds.length === 0) return [];

  // Collect every candidate assignment per physical have record.
  const haveToCandidates = new Map<string, AutoMatchAssignment[]>();
  for (const cid of compositeIds) {
    const dash = cid.indexOf('-');
    if (dash === -1) continue;
    const wantListId = parseInt(cid.substring(0, dash), 10);
    const haveRecordId = cid.substring(dash + 1);
    const details = matchDetails?.[cid];
    if (!details) continue;
    const cand: AutoMatchAssignment = {
      wantListId,
      haveRecordId,
      compositeId: cid,
      cardId: details.cardId,
      foil: details.foil,
      condition: details.condition,
    };
    const list = haveToCandidates.get(haveRecordId);
    if (list) list.push(cand); else haveToCandidates.set(haveRecordId, [cand]);
  }

  // Greedy route. Want capacity seeded from valuedCards.quantity (the receiver's
  // wanted count). Use `?? 1` so an explicit quantity of 0 is honoured (no
  // routes), while undefined defaults to 1.
  const wantRemaining = new Map<number, number>();
  for (const c of valuedCards) {
    wantRemaining.set(c.wantListId, c.quantity ?? 1);
  }

  // Available copies remaining per printing (MTGCard.id), shared across every
  // have-record of the same printing so duplicate records never double-count
  // against the sender's owned quantity.
  const cardRemaining = new Map<number, number>();
  const assignments: AutoMatchAssignment[] = [];
  haveToCandidates.forEach(candidates => {
    const cardId = candidates[0].cardId;
    if (!cardRemaining.has(cardId)) {
      // `?? 1` preserves the legacy one-copy behaviour when the have-list
      // summary hasn't loaded yet.
      cardRemaining.set(cardId, haveQtyByCardId.get(cardId) ?? 1);
    }
    // Route each owned copy to the first candidate want that still has capacity,
    // so the group ends up with min(owned copies, remaining want capacity).
    while ((cardRemaining.get(cardId) ?? 0) > 0) {
      let routed = false;
      for (const cand of candidates) {
        const remaining = wantRemaining.get(cand.wantListId) ?? 0;
        if (remaining > 0) {
          wantRemaining.set(cand.wantListId, remaining - 1);
          cardRemaining.set(cardId, (cardRemaining.get(cardId) ?? 0) - 1);
          assignments.push(cand);
          routed = true;
          break;
        }
      }
      if (!routed) break; // no want has remaining capacity → stop routing this have
    }
  });

  // Group routed assignments by (cardId, foil).
  const groups = new Map<string, AutoMatchAssignment[]>();
  for (const a of assignments) {
    const key = `${a.cardId}|${a.foil ? 'f' : 'n'}`;
    const list = groups.get(key);
    if (list) list.push(a); else groups.set(key, [a]);
  }

  const expanded: AutoMatchedCard[] = [];
  groups.forEach(groupAssignments => {
    const first = groupAssignments[0];
    // Representative valuedCard: prefer one whose acceptableSetDetails includes
    // this printing (gives correct image via resolveAutoMatchImageUrl), else
    // the valuedCard matching the first assignment's wantListId, else any.
    const representative =
      valuedCards.find(c => c.acceptableSetDetails?.some(d => d.cardId === first.cardId))
      ?? valuedCards.find(c => c.wantListId === first.wantListId)
      ?? valuedCards[0];
    if (!representative) return;

    expanded.push({
      ...representative,
      quantity: groupAssignments.length,
      foil: first.foil,
      wantListId: first.wantListId,
      _autoMatchHaveCardId: String(first.cardId),
      _autoMatchCompositeId: first.compositeId,
      _autoMatchAssignments: groupAssignments,
    });
  });

  return expanded;
}

// ── 2. Image Resolution (v5) ───────────────────────────────────────────────

export interface HaveMatchEntry {
  haveCardId: string;
  condition?: string;
  foil?: boolean;
}

export function resolveAutoMatchImageUrl(
  card: AutoMatchedCard,
  haveMatchMap?: Map<string, HaveMatchEntry>,
): string | undefined {
  // Check _autoMatchHaveCardId first (from expansion)
  const storedHaveCardId = card._autoMatchHaveCardId;

  if (storedHaveCardId && card.acceptableSetDetails) {
    const setDetail = card.acceptableSetDetails.find(
      (d) => String(d.cardId) === String(storedHaveCardId)
    );
    if (setDetail?.imageUrl) {
      return setDetail.imageUrl;
    }
  }

  // Fallback: Use haveMatchMap
  if (!haveMatchMap) return undefined;
  const haveMatch = haveMatchMap.get(String(card.wantListId));
  if (!haveMatch) return undefined;
  const setDetail = card.acceptableSetDetails?.find(
    (d) => String(d.cardId) === haveMatch.haveCardId
  );
  return setDetail?.imageUrl;
}

// ── 3. Build Have Match Map ────────────────────────────────────────────────

export function buildHaveMatchMapFromPackage(
  packageMatch: MatchIndexPackage | undefined,
): Map<string, HaveMatchEntry> {
  const map = new Map<string, HaveMatchEntry>();
  if (!packageMatch) return map;

  for (const compositeId of packageMatch.matchingCardIds) {
    const dashIdx = compositeId.indexOf('-');
    if (dashIdx === -1) continue;
    const wantListId = compositeId.substring(0, dashIdx);
    const details = packageMatch.matchDetails?.[compositeId];
    const haveCardId = String(details?.cardId ?? compositeId.substring(dashIdx + 1));
    if (!map.has(wantListId)) {
      map.set(wantListId, {
        haveCardId,
        condition: details?.condition,
        foil: details?.foil,
      });
    }
  }
  return map;
}

// ── 4. Build Modal Trade Card (v7) ─────────────────────────────────────────

export function buildModalTradeCard(
  card: AutoMatchedCard,
  haveMatch: HaveMatchEntry | undefined,
  getSetCode: (setName: string) => string,
  isUUID: (str: string) => boolean,
): ModalTradeCard {
  // Check price availability
  const hasNonFoil = !!(card.nonFoilPointValue && card.nonFoilPointValue > 0);
  const hasFoil = !!(card.foilPointValue && card.foilPointValue > 0);

  let acceptableSetDetails = card.acceptableSetDetails;

  // Only create our own acceptableSetDetails if backend doesn't provide it or it's empty
  if (!acceptableSetDetails || acceptableSetDetails.length === 0) {
    acceptableSetDetails = card.acceptableSets?.map((setName: string, index: number) => {
      const backendCode = (card as any).acceptableSetCodes?.[index];
      if (backendCode) {
        return { name: setName, code: backendCode, cardId: 0 };
      }
      const mappedCode = getSetCode(setName);
      if (mappedCode && mappedCode !== setName) {
        return { name: setName, code: mappedCode, cardId: 0 };
      }
      return { name: setName, code: setName, cardId: 0 };
    });
  }

  // Auto-match: find sender's specific printing in acceptableSetDetails
  let autoMatchSetDetail: SetDetail | undefined;
  if (haveMatch && acceptableSetDetails && acceptableSetDetails.length > 0) {
    autoMatchSetDetail = acceptableSetDetails.find(
      (d: SetDetail) => String(d.cardId) === haveMatch.haveCardId
    );
  }

  // --- SET defaults ---
  let finalSetCode: string = '';
  let finalSetName: string = card.set;
  let finalImageUrl: string = card.imageUrl;
  let finalCardId: number = card.cardId;

  if (autoMatchSetDetail) {
    finalSetCode = autoMatchSetDetail.code;
    finalSetName = autoMatchSetDetail.name;
    if (autoMatchSetDetail.imageUrl) {
      finalImageUrl = autoMatchSetDetail.imageUrl;
    }
    if (autoMatchSetDetail.cardId) {
      finalCardId = autoMatchSetDetail.cardId;
    }
  } else {
    const isSetCodeUUID = card.setCode && isUUID(card.setCode);
    if (!card.setCode || isSetCodeUUID) {
      if (acceptableSetDetails && acceptableSetDetails.length > 0) {
        const currentSetDetail = acceptableSetDetails.find((detail: any) => detail.name === card.set);
        if (currentSetDetail && currentSetDetail.code) {
          finalSetCode = currentSetDetail.code;
        } else {
          finalSetCode = acceptableSetDetails[0].code;
        }
      } else {
        const mapped = getSetCode(card.set);
        if (mapped && mapped !== card.set) {
          finalSetCode = mapped;
        } else {
          finalSetCode = card.set;
        }
      }
    } else {
      finalSetCode = card.setCode;
    }
  }

  // --- FOIL defaults ---
  const acceptsFoilOnly = card.acceptFoil && !card.acceptNonFoil;
  const acceptsNonFoilOnly = !card.acceptFoil && card.acceptNonFoil;

  let defaultFoil: boolean;
  if (haveMatch?.foil !== undefined) {
    if (haveMatch.foil && !acceptsNonFoilOnly) {
      defaultFoil = true;
    } else if (!haveMatch.foil && !acceptsFoilOnly) {
      defaultFoil = false;
    } else {
      defaultFoil = acceptsFoilOnly ? true :
                    acceptsNonFoilOnly ? false :
                    (hasNonFoil ? false : (hasFoil ? true : card.foil));
    }
  } else {
    defaultFoil = acceptsFoilOnly ? true :
                  acceptsNonFoilOnly ? false :
                  (hasNonFoil ? false : (hasFoil ? true : card.foil));
  }

  // --- CONDITION defaults ---
  const acceptableConditions = card.acceptableConditions;
  let defaultCondition = acceptableConditions?.[0] || 'NEAR_MINT';
  if (haveMatch?.condition) {
    if (!acceptableConditions || acceptableConditions.length === 0 ||
        acceptableConditions.includes(haveMatch.condition)) {
      defaultCondition = haveMatch.condition;
    }
  }

  // Calculate initial value based on defaults
  const initialValue = defaultFoil
    ? (card.foilPointValue || card.pointValue)
    : (card.nonFoilPointValue || card.pointValue);

  return {
    id: card.id,
    cardId: finalCardId,
    qty: card.quantity,
    maxQty: card.quantity,
    name: card.name,
    set: finalSetName,
    setCode: finalSetCode || card.set,
    condition: defaultCondition,
    language: card.language,
    foil: defaultFoil,
    edition: card.edition || '',
    image: finalImageUrl,
    imageSmall: finalImageUrl?.replace('/large/', '/small/').replace('/normal/', '/small/'),
    value: initialValue,
    baseValue: card.pointValue,
    foilValue: card.foilPointValue,
    nonFoilValue: card.nonFoilPointValue,
    acceptableSets: card.acceptableSets,
    acceptableSetCodes: (card as any).acceptableSetCodes,
    acceptableSetDetails: acceptableSetDetails,
    wantListId: card.wantListId,
    availableInFoil: hasFoil,
    availableInNonFoil: hasNonFoil,
    acceptFoil: card.acceptFoil,
    acceptNonFoil: card.acceptNonFoil,
    acceptableConditions: card.acceptableConditions,
    scryfallId: card.scryfallId,
    availableLanguages: (card as any).availableLanguages,
    acceptableLanguages: (card as any).acceptableLanguages,
  };
}
